/**
 * ForgotPasswordPage — 3 fluxos backend em auth_recovery.py:
 *   1. Código de recuperação (12 chars XXXX-XXXX-XXXX)
 *   2. Pergunta de segurança
 *   3. Pedido a admin (failsafe quando user perdeu tudo)
 *
 * Design: consistente com LoginPage (paleta Rugged).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mountain, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  bg: '#2B2A25', bg2: '#241E19', bg3: '#0F0D0B',
  brand: '#E87A2A', sand: '#C4A275',
  sandDim: 'rgba(196,162,117,0.22)', sandLine: 'rgba(196,162,117,0.18)',
  ink: '#F2EBDD', inkDim: '#8A7A63', danger: '#EF4444',
  cardBg: 'rgba(26,22,19,0.82)', inputBg: 'rgba(0,0,0,0.35)',
};

const styles = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '32px 16px', background: `linear-gradient(180deg, ${C.bg} 0%, ${C.bg2} 100%)`,
    fontFamily: '"Work Sans", system-ui, sans-serif',
  },
  card: {
    width: '100%', maxWidth: 440, background: C.cardBg, backdropFilter: 'blur(12px)',
    border: `1px solid ${C.sandDim}`, borderRadius: 16, padding: 32,
    boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
  },
  logo: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 },
  logoTitle: { fontFamily: '"Bebas Neue", sans-serif', fontSize: 42, color: C.sand, letterSpacing: 3, marginTop: 8 },
  logoSub: { fontFamily: '"Roboto Mono", monospace', fontSize: 10, color: C.sand, letterSpacing: 4, marginTop: -4 },
  title: { color: C.ink, fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 8 },
  subtitle: { color: C.inkDim, fontSize: 13, textAlign: 'center', marginBottom: 20 },
  label: { color: C.sand, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, marginBottom: 6, display: 'block' },
  input: {
    width: '100%', padding: '12px 14px', background: C.inputBg,
    border: `1px solid ${C.sandDim}`, borderRadius: 8, color: C.ink, fontSize: 14, outline: 'none',
    marginBottom: 14, boxSizing: 'border-box',
  },
  btn: {
    width: '100%', padding: '14px', background: C.brand, color: '#fff', border: 'none',
    borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3,
    marginTop: 4,
  },
  btnGhost: {
    width: '100%', padding: '12px', background: 'transparent', color: C.sand,
    border: `1px solid ${C.sandLine}`, borderRadius: 10, fontSize: 13, cursor: 'pointer',
    marginTop: 10,
  },
  error: { color: C.danger, fontSize: 12, textAlign: 'center', marginTop: 10 },
  hint: { color: C.inkDim, fontSize: 11, marginBottom: 10, lineHeight: 1.5 },
  backLink: {
    display: 'flex', alignItems: 'center', gap: 6, color: C.sand, fontSize: 13,
    textDecoration: 'none', marginTop: 20, justifyContent: 'center', cursor: 'pointer',
    background: 'transparent', border: 'none',
  },
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'method' | 'code' | 'question' | 'done'
  const [email, setEmail] = useState('');
  const [methods, setMethods] = useState([]);
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [code, setCode] = useState('');
  const [answer, setAnswer] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookupMethods = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) { setError('Insere o email'); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/auth/security-question`, { params: { email: em } });
      setMethods(data.methods || []);
      setSecurityQuestion(data.security_question || '');
      setStep('method');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao consultar');
    } finally { setLoading(false); }
  };

  const resetWithCode = async (e) => {
    e.preventDefault();
    if (newPass !== confirmPass) { setError('As passwords não coincidem'); return; }
    if (newPass.length < 6) { setError('Password precisa 6+ caracteres'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-with-code`, {
        email: email.trim().toLowerCase(), recovery_code: code, new_password: newPass,
      });
      toast.success('Password redefinida! Já podes entrar.');
      setStep('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao redefinir');
    } finally { setLoading(false); }
  };

  const resetWithQuestion = async (e) => {
    e.preventDefault();
    if (newPass !== confirmPass) { setError('As passwords não coincidem'); return; }
    if (newPass.length < 6) { setError('Password precisa 6+ caracteres'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-with-question`, {
        email: email.trim().toLowerCase(), answer, new_password: newPass,
      });
      toast.success('Password redefinida! Já podes entrar.');
      setStep('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao redefinir');
    } finally { setLoading(false); }
  };

  const requestAdminReset = async () => {
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/request-admin-reset`, { email: email.trim().toLowerCase() });
      toast.success('Pedido enviado');
      setStep('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro no pedido');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card} data-testid="forgot-password-card">
        <div style={styles.logo}>
          <Mountain size={44} color={C.brand} strokeWidth={2} />
          <div style={styles.logoTitle}>TECTUR 4×4</div>
          <div style={styles.logoSub}>NAVIGATION</div>
        </div>

        {step === 'email' && (
          <>
            <div style={styles.title}>Recuperar acesso</div>
            <div style={styles.subtitle}>Insere o email da tua conta para escolheres um método de recuperação.</div>
            <form onSubmit={lookupMethods}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                style={styles.input}
                data-testid="forgot-email-input"
                required
              />
              <button type="submit" disabled={loading} style={styles.btn} data-testid="forgot-lookup-btn">
                {loading ? 'A consultar…' : 'Continuar'}
              </button>
              {error && <div style={styles.error}>{error}</div>}
            </form>
          </>
        )}

        {step === 'method' && (
          <>
            <div style={styles.title}>Escolhe um método</div>
            <div style={styles.subtitle}>Como queres recuperar a tua password para <b>{email}</b>?</div>
            {methods.includes('code') && (
              <button style={styles.btn} onClick={() => setStep('code')} data-testid="method-code-btn">
                🔑 Usar código de recuperação
              </button>
            )}
            {methods.includes('question') && (
              <button style={{...styles.btn, marginTop: 12}} onClick={() => setStep('question')} data-testid="method-question-btn">
                ❓ Responder pergunta de segurança
              </button>
            )}
            {methods.length === 0 && (
              <div style={styles.hint}>
                Não encontrámos métodos de auto-recuperação configurados para este email. Podes pedir a um admin para redefinir a tua password manualmente.
              </div>
            )}
            <button
              style={{...styles.btnGhost, marginTop: methods.length ? 20 : 0}}
              onClick={requestAdminReset}
              disabled={loading}
              data-testid="method-admin-btn"
            >
              📞 Pedir reset a um admin
            </button>
            {error && <div style={styles.error}>{error}</div>}
          </>
        )}

        {step === 'code' && (
          <>
            <div style={styles.title}>Código de recuperação</div>
            <div style={styles.subtitle}>Insere o código que guardaste quando configuraste a recuperação.</div>
            <form onSubmit={resetWithCode}>
              <label style={styles.label}>Código (XXXX-XXXX-XXXX)</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD-1234-EFGH"
                style={{...styles.input, fontFamily: 'monospace', letterSpacing: 2, textTransform: 'uppercase'}}
                data-testid="forgot-code-input"
                required
              />
              <label style={styles.label}>Nova password</label>
              <input
                type="password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                style={styles.input}
                minLength={6}
                data-testid="forgot-newpass-input"
                required
              />
              <label style={styles.label}>Confirmar</label>
              <input
                type="password"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                style={styles.input}
                minLength={6}
                data-testid="forgot-confirmpass-input"
                required
              />
              <button type="submit" disabled={loading} style={styles.btn} data-testid="forgot-reset-code-btn">
                {loading ? 'A redefinir…' : 'Redefinir password'}
              </button>
              {error && <div style={styles.error}>{error}</div>}
            </form>
          </>
        )}

        {step === 'question' && (
          <>
            <div style={styles.title}>Pergunta de segurança</div>
            <div style={styles.subtitle}><b>{securityQuestion}</b></div>
            <form onSubmit={resetWithQuestion}>
              <label style={styles.label}>Resposta</label>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                style={styles.input}
                data-testid="forgot-answer-input"
                required
              />
              <label style={styles.label}>Nova password</label>
              <input
                type="password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                style={styles.input}
                minLength={6}
                data-testid="forgot-q-newpass-input"
                required
              />
              <label style={styles.label}>Confirmar</label>
              <input
                type="password"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                style={styles.input}
                minLength={6}
                data-testid="forgot-q-confirmpass-input"
                required
              />
              <button type="submit" disabled={loading} style={styles.btn} data-testid="forgot-reset-question-btn">
                {loading ? 'A redefinir…' : 'Redefinir password'}
              </button>
              {error && <div style={styles.error}>{error}</div>}
            </form>
          </>
        )}

        {step === 'done' && (
          <>
            <div style={styles.title}>✅ Concluído</div>
            <div style={styles.subtitle}>Se o email existir, a password foi redefinida ou o pedido foi criado. Podes voltar ao login.</div>
            <button style={styles.btn} onClick={() => navigate('/login')} data-testid="forgot-back-login-btn">
              Voltar ao login
            </button>
          </>
        )}

        <button type="button" style={styles.backLink} onClick={() => navigate('/login')} data-testid="forgot-back-btn">
          <ArrowLeft size={14} /> Voltar
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
